/**
 * p-değeri yeniden hesaplama.
 *
 * Raporda bildirilen test istatistiği (t, F, χ², r, z) ve serbestlik
 * derecelerinden p değerini jStat ile yeniden hesaplar. Böylece "yazılan p,
 * istatistikle tutarlı mı?" sorusu cevaplanır.
 *
 * Önemli: yeniden hesaplama yalnızca testin yönü ve df'si netse anlamlıdır.
 * Eksik bilgide `null` döneriz; asla tahminle "hata" üretmeyiz.
 */

import jStat from 'jstat';

export interface RecomputedP {
	p: number;
	/** Hesaplamanın dayandığı formül (rapora yazılır). */
	formula: string;
	/** Testin yönü. */
	tails: 1 | 2;
}

export interface RecomputeInput {
	kind: 't' | 'F' | 'chi2' | 'r' | 'z';
	value: number;
	df1?: number;
	df2?: number;
	n?: number;
	tails?: 1 | 2;
}

/**
 * p değerini yeniden hesaplar. Girdi yetersizse neden belirtilir.
 */
export function recomputeP(input: RecomputeInput): { ok: true; result: RecomputedP } | { ok: false; reason: string } {
	const { kind, value, df1, df2, n } = input;
	const tails = input.tails ?? 2;

	if (!Number.isFinite(value)) return { ok: false, reason: 'İstatistik değeri okunamadı.' };

	switch (kind) {
		case 't': {
			if (!isPositive(df1)) return { ok: false, reason: 't testi için serbestlik derecesi (df) yok.' };
			if (Math.abs(value) > 100) {
				return { ok: false, reason: 't değeri olağan dışı büyük; çıkarım hatası olabilir.' };
			}
			const oneTail = jStat.studentt.cdf(-Math.abs(value), df1);
			const p = clamp01(tails === 2 ? 2 * oneTail : oneTail);
			return {
				ok: true,
				result: {
					p,
					tails,
					formula: `t(${df1}) = ${fmt(value)}, ${tails === 2 ? 'iki' : 'tek'} yönlü → p = ${fmtP(p)}`
				}
			};
		}
		case 'F': {
			if (!isPositive(df1) || !isPositive(df2)) {
				return { ok: false, reason: 'F testi için iki serbestlik derecesi gerekli.' };
			}
			if (value < 0) return { ok: false, reason: 'F değeri negatif olamaz.' };
			const p = clamp01(1 - jStat.centralF.cdf(value, df1, df2));
			return {
				ok: true,
				result: {
					p,
					tails: 1,
					formula: `F(${df1}, ${df2}) = ${fmt(value)} → p = ${fmtP(p)}`
				}
			};
		}
		case 'chi2': {
			if (!isPositive(df1)) {
				return { ok: false, reason: 'χ² için serbestlik derecesi gerekli.' };
			}
			if (value < 0) return { ok: false, reason: 'χ² değeri negatif olamaz.' };
			const p = clamp01(1 - jStat.chisquare.cdf(value, df1));
			return {
				ok: true,
				result: { p, tails: 1, formula: `χ²(${df1}) = ${fmt(value)} → p = ${fmtP(p)}` }
			};
		}
		case 'r': {
			// r → t = r * sqrt((n-2)/(1-r²)), df = n-2
			const nn = isPositive(n) ? n : isPositive(df1) ? df1 + 2 : undefined;
			if (!nn || nn < 3) {
				return { ok: false, reason: 'r için örneklem büyüklüğü (n) gerekli.' };
			}
			if (Math.abs(value) >= 1) {
				return { ok: false, reason: 'r değeri ±1 aralığında olmalı.' };
			}
			const r2 = value * value;
			if (r2 >= 1) return { ok: false, reason: 'r değeri ±1’e çok yakın; hesaplama kararsız.' };
			const t = value * Math.sqrt((nn - 2) / (1 - r2));
			const oneTail = jStat.studentt.cdf(-Math.abs(t), nn - 2);
			const p = clamp01(tails === 2 ? 2 * oneTail : oneTail);
			return {
				ok: true,
				result: {
					p,
					tails,
					formula: `r = ${fmt(value)}, n = ${nn} → t(${nn - 2}) = ${fmt(t)} → p = ${fmtP(p)}`
				}
			};
		}
		case 'z': {
			const oneTail = jStat.normal.cdf(-Math.abs(value), 0, 1);
			const p = clamp01(tails === 2 ? 2 * oneTail : oneTail);
			return {
				ok: true,
				result: {
					p,
					tails,
					formula: `z = ${fmt(value)}, ${tails === 2 ? 'iki' : 'tek'} yönlü → p = ${fmtP(p)}`
				}
			};
		}
	}
}

/** p değerini APA biçiminde yazar: p < .001 / p = .032 */
export function formatP(p: number): string {
	if (p < 0.001) return 'p < .001';
	if (p > 0.999) return 'p > .999';
	return `p = ${p.toFixed(3).replace(/^0/, '')}`;
}

function fmt(v: number): string {
	return Number.isInteger(v) ? String(v) : v.toFixed(2);
}

function fmtP(p: number): string {
	return formatP(p);
}

function clamp01(v: number): number {
	if (!Number.isFinite(v)) return Number.NaN;
	return Math.max(0, Math.min(1, v));
}

function isPositive(v: number | undefined): v is number {
	return typeof v === 'number' && Number.isFinite(v) && v > 0;
}

/**
 * İki p değerinin "aynı sonucu" verip vermediğini test eder.
 *
 * - Biri <.05 diğeri >=.05 ise eşik geçişi (threshold flip) vardır.
 * - Aksi halde log10 ölçeğinde tolerans kadar fark kabul edilir.
 */
export interface PComparison {
	consistent: boolean;
	thresholdFlip: boolean;
	/** |log10(p1) - log10(p2)| */
	logDelta: number;
}

export function compareP(
	reported: number,
	recomputed: number,
	options: { alpha?: number; tolerance?: number } = {}
): PComparison {
	const alpha = options.alpha ?? 0.05;
	const tolerance = options.tolerance ?? 0.3; // log10 birim ≈ 2x

	if (!Number.isFinite(reported) || !Number.isFinite(recomputed)) {
		return { consistent: false, thresholdFlip: false, logDelta: Number.NaN };
	}

	const reportedSig = reported < alpha;
	const recomputedSig = recomputed < alpha;
	const thresholdFlip = reportedSig !== recomputedSig;

	// p çok küçükse log farkı büyür; mutlak fark da kontrol edilir.
	const logDelta = Math.abs(
		Math.log10(Math.max(reported, 1e-12)) - Math.log10(Math.max(recomputed, 1e-12))
	);
	const absDelta = Math.abs(reported - recomputed);
	const consistent = !thresholdFlip && (logDelta <= tolerance || absDelta <= 0.005);

	return { consistent, thresholdFlip, logDelta };
}
